/**
 * Phase 7.5.5: ClientContactSelect Component
 *
 * Reusable component for selecting organization contacts.
 * Features:
 * - Auto-selects primary contact by default
 * - Shows loading state while fetching
 * - Disabled when no organization selected
 * - Clears selection when organization changes
 */

import { useEffect } from 'react';
import { useOrganizationContacts } from '@/hooks/useOrganizationContacts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

export interface ClientContactSelectProps {
  organizationId: string | undefined;
  value?: string | null;
  onValueChange: (value: string | null) => void;
  autoSelectPrimary?: boolean; // Default true
  label?: string; // Default "Contact"
  optional?: boolean; // Default true (shows "(Optional)" in label)
  id?: string;
}

export function ClientContactSelect({
  organizationId,
  value,
  onValueChange,
  autoSelectPrimary = true,
  label = 'Contact',
  optional = true,
  id = 'client-contact',
}: ClientContactSelectProps) {
  // Fetch contacts for selected organization
  const { data: contactsData, isLoading } = useOrganizationContacts(organizationId, {
    enabled: !!organizationId,
  });

  // Auto-select primary contact when contacts load
  useEffect(() => {
    if (!autoSelectPrimary || !contactsData?.contacts || isLoading) {
      return;
    }

    // Find primary contact
    const primaryContact = contactsData.contacts.find(c => c.is_primary_contact);

    if (primaryContact && !value) {
      // Only auto-select if no value is currently set
      onValueChange(primaryContact.id);
    }
  }, [contactsData, isLoading, autoSelectPrimary, value, onValueChange]);

  // Determine the select value and state
  // When disabled (no org), show special "disabled" value to display correct text
  // When loading, show "loading" value
  // Otherwise show selected value or 'none'
  const selectValue = !organizationId
    ? 'disabled'
    : isLoading
      ? 'loading'
      : (value || 'none');

  // Determine if disabled
  const disabled = !organizationId || isLoading;

  return (
    <div>
      {/* Only render label if label prop is provided (not empty string) */}
      {label && (
        <Label htmlFor={id}>
          {label} {optional && <span className="text-gray-500 text-sm">(Optional)</span>}
        </Label>
      )}
      <Select
        value={selectValue}
        onValueChange={(val) => onValueChange(val === 'none' ? null : val)}
        disabled={disabled}
      >
        <SelectTrigger id={id} aria-label={label || 'Contact'}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {!organizationId ? (
            <SelectItem value="disabled">Select client first</SelectItem>
          ) : isLoading ? (
            <SelectItem value="loading">Loading contacts...</SelectItem>
          ) : (
            <>
              <SelectItem value="none">No contact</SelectItem>
              {contactsData?.contacts?.map((contact) => (
                <SelectItem key={contact.id} value={contact.id}>
                  {contact.name}{contact.is_primary_contact ? ' (Primary)' : ''}
                </SelectItem>
              ))}
            </>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
